import React from 'react';
import { Navbar } from '../components/Navbar';
import { 
  Tree, 
  Drop, 
  Fire, 
  Buildings, 
  Lightning, 
  Cube,
  Plant,
  ChartLine,
  GlobeHemisphereWest,
  Crosshair
} from '@phosphor-icons/react';

export const ProductSuitesPage = () => {
  const suites = [
    {
      id: 'terra-veritas',
      name: 'TerraVeritas',
      tagline: 'Environment & Climate Intelligence',
      color: '#22C55E',
      icon: Tree,
      sdgs: [13, 15, 6],
      tools: [
        { name: 'CarbonScope Sentinel', description: 'Real-time carbon stock monitoring and verification', icon: Tree },
        { name: 'AquaWatch AI', description: 'Water body tracking and quality assessment', icon: Drop },
        { name: 'FireShield Predictor', description: 'Wildfire risk prediction and early warning', icon: Fire }
      ],
      features: [
        'Multi-spectral satellite fusion',
        'Automated change detection',
        'Carbon credit verification',
        'Deforestation alerts'
      ]
    },
    {
      id: 'urb-intellect',
      name: 'UrbIntellect',
      tagline: 'Smart Cities & Infrastructure',
      color: '#3B82F6',
      icon: Buildings,
      sdgs: [11, 9, 7],
      tools: [
        { name: 'UrbanSight AI', description: 'City-scale planning and zoning analytics', icon: Buildings },
        { name: 'GridGuardian AI', description: 'Energy grid optimization and monitoring', icon: Lightning },
        { name: 'LogixSphere', description: 'Digital twin for urban infrastructure', icon: Cube }
      ],
      features: [
        'LiDAR-powered 3D models',
        'Traffic flow optimization',
        'Infrastructure health monitoring',
        'Smart grid analytics'
      ]
    },
    {
      id: 'agri-grow',
      name: 'AgriGrow',
      tagline: 'Precision Agriculture & Food Security',
      color: '#F5A623',
      icon: Plant,
      sdgs: [2, 12],
      tools: [
        { name: 'CropHealth AI', description: 'NDVI-based crop stress detection', icon: Plant },
        { name: 'TraceHarvest', description: 'Blockchain supply chain tracking', icon: ChartLine },
        { name: 'YieldWise AI', description: 'Predictive yield optimization', icon: Crosshair }
      ],
      features: [
        'Multi-temporal NDVI analysis',
        'Soil moisture prediction',
        'Pest & disease detection',
        'Harvest forecasting'
      ]
    },
    {
      id: 'geo-core',
      name: 'GeoCore',
      tagline: 'Universal Geospatial Intelligence',
      color: '#FFFFFF',
      icon: GlobeHemisphereWest,
      sdgs: [16, 1, 5, 10],
      tools: [
        { name: 'Natural Language GeoAI', description: 'Chat-based spatial queries', icon: GlobeHemisphereWest },
        { name: 'Multi-Domain Fusion', description: 'Satellite, aerial, IoT integration', icon: Cube },
        { name: 'API Playground', description: 'Developer tools and SDKs', icon: ChartLine }
      ],
      features: [
        'Agentic AI assistant',
        'Real-time data streams',
        'Custom API endpoints',
        'WebGIS embeds'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="product-suites-page">
      <Navbar />
      
      <div className="max-w-[1920px] mx-auto p-6 space-y-8">
        {/* Header */}
        <div className="text-center py-12">
          <h1 className="text-5xl md:text-6xl font-black text-white mb-4 tracking-tight">
            Product Suites
          </h1>
          <p className="text-lg text-gray-400 max-w-3xl mx-auto">
            Four specialized intelligence platforms for comprehensive sustainability monitoring
          </p>
        </div>

        {/* Suites */}
        <div className="space-y-8">
          {suites.map((suite) => {
            const SuiteIcon = suite.icon;
            return (
              <div
                key={suite.id}
                className="glass-panel rounded-sm p-8 border-l-4"
                style={{ borderLeftColor: suite.color }}
                data-testid={`suite-${suite.id}`}
              >
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                  {/* Suite Header */}
                  <div className="lg:col-span-3">
                    <div className="flex items-start gap-4 mb-4">
                      <div className="p-4 rounded-sm" style={{ backgroundColor: `${suite.color}20`, border: `1px solid ${suite.color}40` }}>
                        <SuiteIcon size={40} weight="duotone" style={{ color: suite.color }} />
                      </div>
                      <div className="flex-1">
                        <h2 className="text-3xl font-black text-white mb-2 tracking-tight">
                          {suite.name}
                        </h2>
                        <p className="text-gray-400 mb-3">{suite.tagline}</p>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-xs text-gray-500 uppercase tracking-wider">Supports SDGs:</span>
                          {suite.sdgs.map(sdg => (
                            <span
                              key={sdg}
                              className="px-2 py-1 bg-white/10 rounded text-xs font-bold"
                              style={{ color: suite.color }}
                            >
                              {sdg}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Tools */}
                  <div className="lg:col-span-2">
                    <h3 className="text-sm text-gray-400 uppercase tracking-wider mb-4">Core Tools</h3>
                    <div className="space-y-4">
                      {suite.tools.map((tool) => {
                        const ToolIcon = tool.icon;
                        return (
                          <div key={tool.name} className="flex items-start gap-3 p-4 bg-white/5 rounded-sm border border-white/10">
                            <ToolIcon size={24} weight="duotone" style={{ color: suite.color }} className="flex-shrink-0 mt-1" />
                            <div>
                              <h4 className="font-bold text-white mb-1">{tool.name}</h4>
                              <p className="text-sm text-gray-400">{tool.description}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Features */}
                  <div>
                    <h3 className="text-sm text-gray-400 uppercase tracking-wider mb-4">Key Features</h3>
                    <ul className="space-y-2">
                      {suite.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm text-gray-300">
                          <span className="text-emerald-500 mt-1">•</span>
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* CTA */}
        <div className="glass-panel rounded-sm p-8 text-center">
          <h3 className="text-2xl font-bold text-white mb-3">Ready to explore?</h3>
          <p className="text-gray-400 mb-6">Access all product suites through the interactive map</p>
          <a
            href="/map"
            className="inline-block px-6 py-3 bg-emerald-500 hover:bg-emerald-600 text-black font-bold rounded-sm transition-colors"
            data-testid="cta-explore-map"
          >
            Launch Interactive Map
          </a>
        </div>
      </div>
    </div>
  );
};
